/**
 * empStatus.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source-of-truth for employee status logic (UI only — no data changes).
 *
 * Rules:
 *  1. On OD                           → "od"
 *  2. Currently checked in            → "present"
 *  3. Has checkOutTime today          → "checked-out"  (checked in & left, incl. 7 PM auto)
 *  4. No check-in AND time ≥ 10:00 AM IST → "absent"   (missed the 10 AM deadline)
 *  5. No check-in AND time < 10:00 AM IST → "pending"  (still early, may come in)
 */

/**
 * Returns the current IST hour (0-23).
 */
function getISTHour() {
  return parseInt(
    new Date().toLocaleTimeString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      hour12: false,
    }),
    10
  );
}

/**
 * Derives the display status for a single employee object.
 *
 * @param {object} emp  - Employee object from AppContext (team array).
 * @returns {{ status: string, checkOutTime: string|null }}
 *   status: one of "od" | "present" | "checked-out" | "absent" | "pending"
 *   checkOutTime: the checkout time string (only set when status === "checked-out")
 */
export function getEmpStatus(emp) {
  if (emp.onOD) {
    return { status: "od", checkOutTime: null };
  }
  if (emp.checkedIn) {
    return { status: "present", checkOutTime: null };
  }
  if (emp.checkOutTime) {
    // Checked in at some point today and then checked out (manual or auto 7 PM)
    return { status: "checked-out", checkOutTime: emp.checkOutTime };
  }
  // Never checked in — decide based on 10 AM IST deadline
  const istHour = getISTHour();
  if (istHour >= 10) {
    return { status: "absent", checkOutTime: null };
  }
  // Before 10 AM — not yet late, still pending
  return { status: "pending-arrival", checkOutTime: null };
}

/**
 * Returns true if the employee is considered "absent" today
 * (never checked in and it is past 10:00 AM IST).
 */
export function isEmpAbsent(emp) {
  return !emp.checkedIn && !emp.onOD && !emp.checkOutTime && getISTHour() >= 10;
}
